import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, TextField, MenuItem,
  Alert, CircularProgress, Grid,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { employeeService, staffService } from '@smartgrow/shared';
import type { PaymentMethod } from '@smartgrow/shared/types';

export const EditEmployeePage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: employee, isLoading } = useQuery({
    queryKey: ['employees', id],
    queryFn: () => employeeService.getEmployeeById(id!),
    enabled: !!id,
  });

  const { data: staffData } = useQuery({
    queryKey: ['staff-all'],
    queryFn: () => staffService.getStaff({ size: 200 }),
  });
  const staff = staffData?.content ?? [];

  const [form, setForm] = useState({
    employeeNumber: '',
    fullName: '',
    email: '',
    phone: '',
    position: '',
    monthlySalary: 0,
    paymentDay: 25,
    paymentMethod: 'BANK_TRANSFER' as PaymentMethod,
    bankAccountNumber: '',
    bankName: '',
    hireDate: '',
    userId: undefined as string | undefined,
    notes: '',
  });

  useEffect(() => {
    if (employee) {
      setForm({
        employeeNumber: employee.employeeNumber,
        fullName: employee.fullName,
        email: employee.email ?? '',
        phone: employee.phone ?? '',
        position: employee.position ?? '',
        monthlySalary: employee.monthlySalary,
        paymentDay: employee.paymentDay,
        paymentMethod: employee.paymentMethod,
        bankAccountNumber: employee.bankAccountNumber ?? '',
        bankName: employee.bankName ?? '',
        hireDate: employee.hireDate?.split('T')[0] ?? '',
        userId: employee.userId,
        notes: employee.notes ?? '',
      });
    }
  }, [employee]);

  const mutation = useMutation({
    mutationFn: (data: typeof form) =>
      employeeService.updateEmployee(id!, {
        ...data,
        monthlySalary: Number(data.monthlySalary),
        paymentDay: Number(data.paymentDay),
        userId: data.userId || undefined,
        email: data.email || undefined,
        phone: data.phone || undefined,
        position: data.position || undefined,
        bankAccountNumber: data.bankAccountNumber || undefined,
        bankName: data.bankName || undefined,
        notes: data.notes || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employees'] });
      navigate('/dashboard/employees');
    },
  });

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.fullName.trim()) e.fullName = 'Full name is required';
    if (!form.monthlySalary || Number(form.monthlySalary) <= 0) e.monthlySalary = 'Salary must be greater than 0';
    if (!form.hireDate) e.hireDate = 'Hire date is required';
    const day = Number(form.paymentDay);
    if (!day || day < 1 || day > 28) e.paymentDay = 'Payment day must be between 1 and 28';
    if (form.paymentMethod === 'BANK_TRANSFER' && !form.bankAccountNumber?.trim())
      e.bankAccountNumber = 'Bank account is required for bank transfer';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const set = (field: string, value: any) => {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: undefined as any }));
  };

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }

  if (!employee) {
    return <Alert severity="error">Employee not found.</Alert>;
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} variant="outlined" size="large">Back</Button>
        <Box>
          <Typography variant="h4" fontWeight="bold">Edit Employee</Typography>
          <Typography variant="body2" color="text.secondary">{employee.employeeNumber} — {employee.fullName}</Typography>
        </Box>
      </Box>

      {mutation.isError && (
        <Alert severity="error" sx={{ mb: 3 }}>Failed to update employee. Please try again.</Alert>
      )}

      <Paper sx={{ p: { xs: 2, sm: 4 }, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>Personal Information</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Employee Number"
              value={form.employeeNumber}
              fullWidth
              disabled
              helperText="Employee number cannot be changed"
              size="medium"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Full Name *"
              value={form.fullName}
              onChange={(e) => set('fullName', e.target.value)}
              fullWidth error={!!errors.fullName} helperText={errors.fullName} size="medium"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Position / Role"
              value={form.position}
              onChange={(e) => set('position', e.target.value)}
              fullWidth size="medium"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Hire Date *"
              type="date"
              value={form.hireDate}
              onChange={(e) => set('hireDate', e.target.value)}
              fullWidth error={!!errors.hireDate} helperText={errors.hireDate}
              InputLabelProps={{ shrink: true }} size="medium"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Email" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} fullWidth size="medium" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Phone" value={form.phone} onChange={(e) => set('phone', e.target.value)} fullWidth size="medium" />
          </Grid>
          <Grid item xs={12}>
            <TextField
              select label="Link to Staff Account (optional)"
              value={form.userId ?? ''}
              onChange={(e) => set('userId', e.target.value || undefined)}
              fullWidth size="medium"
            >
              <MenuItem value="">None</MenuItem>
              {staff.map((s) => (
                <MenuItem key={s.id} value={s.id}>{s.fullName} (@{s.username})</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: { xs: 2, sm: 4 }, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>Salary & Payment</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Monthly Salary (₦) *" type="number"
              value={form.monthlySalary || ''}
              onChange={(e) => set('monthlySalary', Number.parseFloat(e.target.value) || 0)}
              fullWidth error={!!errors.monthlySalary} helperText={errors.monthlySalary}
              inputProps={{ min: 1, step: 1000 }} size="medium"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Payment Day of Month *" type="number"
              value={form.paymentDay}
              onChange={(e) => set('paymentDay', Number.parseInt(e.target.value) || 25)}
              fullWidth error={!!errors.paymentDay} helperText={errors.paymentDay || '1–28'}
              inputProps={{ min: 1, max: 28 }} size="medium"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select label="Payment Method *"
              value={form.paymentMethod}
              onChange={(e) => set('paymentMethod', e.target.value as PaymentMethod)}
              fullWidth size="medium"
            >
              <MenuItem value="BANK_TRANSFER">Bank Transfer</MenuItem>
              <MenuItem value="CASH">Cash</MenuItem>
              <MenuItem value="MOBILE_MONEY">Mobile Money</MenuItem>
              <MenuItem value="CHEQUE">Cheque</MenuItem>
            </TextField>
          </Grid>

          {form.paymentMethod === 'BANK_TRANSFER' && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Bank Account Number *" value={form.bankAccountNumber}
                  onChange={(e) => set('bankAccountNumber', e.target.value)}
                  fullWidth error={!!errors.bankAccountNumber} helperText={errors.bankAccountNumber} size="medium"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Bank Name" value={form.bankName} onChange={(e) => set('bankName', e.target.value)} fullWidth size="medium" />
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <TextField
              label="Notes (optional)" multiline rows={3}
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              fullWidth inputProps={{ maxLength: 500 }} size="medium"
            />
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', pb: 4 }}>
        <Button size="large" onClick={() => navigate('/dashboard/employees')} disabled={mutation.isPending}>Cancel</Button>
        <Button
          size="large" variant="contained"
          onClick={() => validate() && mutation.mutate(form)}
          disabled={mutation.isPending}
          startIcon={mutation.isPending ? <CircularProgress size={20} /> : null}
          sx={{ minWidth: 160 }}
        >
          {mutation.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>
    </Box>
  );
};
